import React from 'react';

import { NotificationAddOutlined } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Typography, useMediaQuery, useTheme, Tooltip } from '@mui/material';

import { useIntl } from '@kamernet/core/Intl';
import { PageLayout } from '@kamernet/core/PageLayout';

import { ListingSearchBar } from '@kamernet/modules/Listing/components/ListingSearchBar';
import { useFilterSelectors } from '@kamernet/modules/Listing/context/hooks/useFilterSelectors';

import { useSearchAlert } from '../../hooks/useSearchAlertHandling';
import { FilterForm } from '../FilterForm';
import { AlertSnackbar } from '../Snackbar/Snackbar';
import { ToggleView } from '../ToggleView';

import styles from './Search.module.css';

export const Search = () => {
  const { T } = useIntl();
  const isValidCity = useFilterSelectors().isValidCity();
  const {
    isSavingAlert,
    onSearchAlertClick,
    searchAlertNotification,
    handleCloseAlertSnackbar,
  } = useSearchAlert();
  const theme = useTheme();
  const isMediaSmDown = useMediaQuery(theme.breakpoints.down('sm'));

  const [isLocationRequired, setIsLocationRequired] = React.useState(false);

  const onSearchAlertClickInternal = React.useCallback(async () => {
    if (!isValidCity) {
      setIsLocationRequired(true);
    } else {
      await onSearchAlertClick();
    }
  }, [isValidCity, onSearchAlertClick]);

  const resetLocationError = React.useCallback(() => {
    setIsLocationRequired(false);
  }, []);

  return (
    <PageLayout.SectionContainer className={styles.root}>
      <div className={styles.sectionContainer}>
        <ListingSearchBar
          validationMessageStrategy="always"
          className={styles.listingSearchBar}
          isLocationRequired={isLocationRequired}
          onLocationInputChange={resetLocationError}
        />

        {isMediaSmDown ? (
          <React.Fragment>
            <div className={styles.subSearchLine}>
              <Tooltip title={!isValidCity ? T('ERR_LOCATION_IS_MISSING') : ''}>
                <LoadingButton
                  variant="contained"
                  size="small"
                  className={styles.saveAlert}
                  loading={isSavingAlert}
                  onClick={onSearchAlertClickInternal}
                >
                  <NotificationAddOutlined />
                  <Typography variant="body2">
                    {T('cta.create_search_alert')}
                  </Typography>
                </LoadingButton>
              </Tooltip>

              <FilterForm filterViewPosition="bottom" />
            </div>
            <ToggleView />
          </React.Fragment>
        ) : (
          <div className={styles.subSearchLine}>
            <ToggleView />
            <section className={styles.container}>
              <Tooltip title={!isValidCity ? T('ERR_LOCATION_IS_MISSING') : ''}>
                <LoadingButton
                  variant="contained"
                  className={styles.saveAlert}
                  loading={isSavingAlert}
                  onClick={onSearchAlertClickInternal}
                >
                  <NotificationAddOutlined />
                  <Typography variant="body2">
                    {T('cta.create_search_alert')}
                  </Typography>
                </LoadingButton>
              </Tooltip>

              <FilterForm filterViewPosition="right" />
            </section>
          </div>
        )}
      </div>
      {searchAlertNotification && (
        <AlertSnackbar
          notification={searchAlertNotification}
          onClose={handleCloseAlertSnackbar}
        />
      )}
    </PageLayout.SectionContainer>
  );
};
